import React, {
  useCallback, useContext, useState,
} from 'react';
import {
  Button, Col, Modal, Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import validateForm from 'utils/Validator';
import Form from 'components/Form/Layout';
import { yupCreateDocRequestTemplate } from 'utils/yupFields';
import { LoadingContext } from 'context/Loading';
import TextField from 'components/Form/FormTextField';
import {
  AdminDocRequestTemplateForm, DocRequestTemplateCategoryType,
  DocRequestTemplateProps,
} from 'types/DocRequestTemplate';
import handlePromise from 'utils/Promise';
import DocsRequestTemplatesApi from 'services/api/DocsRequestTemplates';
import Checkbox from 'components/Form/FormCheckbox';
import SuccessMessage from '../SuccessMessage';

type CreateDocRequestTemplateModalProps = {
  data?: DocRequestTemplateProps,
  category: DocRequestTemplateCategoryType
  onClose: () => void,
  reloadData: () => void,
};

function CreateDocRequestTemplateModal( {
  data, category, onClose, reloadData,
}: CreateDocRequestTemplateModalProps ) {
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );
  const [formSubmitted, setFormSubmitted] = useState( false );
  const validate = validateForm( yupCreateDocRequestTemplate );

  const submitForm = useCallback( async ( formValues: AdminDocRequestTemplateForm ) => {
    toggleLoading( true );

    const dataToSend = {
      nombre: formValues.nombre,
      descripcion: formValues.descripcion,
      categoria: category,
      estado: 'ABIERTA',
      // requerido: formValues.requerido,
    };

    const [, response] = await handlePromise(
      data?.plantillaId
        ? DocsRequestTemplatesApi.editTemplate( data?.plantillaId, dataToSend )
        : DocsRequestTemplatesApi.createTemplate( dataToSend ),
    );

    if ( response.ok ) {
      setFormSubmitted( true );
      reloadData();
    }
    toggleLoading( false );
    // ToDo show error
  }, [category] );

  return (
    <Modal
      show
      centered
      size="lg"
      onHide={onClose}
    >
      <Modal.Header className="pb-0" closeButton />
      <Modal.Body className="pt-0">
        <Row>
          <Col md={11} className="m-auto">
            <h3 className="text-center mb-4">{data?.plantillaId ? t( 'admin.doctemplate.edit' ) : t( 'admin.doctemplate.create' )}</h3>
            {formSubmitted
              ? (
                <SuccessMessage
                  description={data?.plantillaId
                    ? t( 'admin.doctemplate.editSuccess' ) : t( 'admin.doctemplate.createSuccess' )}
                  onClose={onClose}
                />
              )
              : (
                <Form
                  validate={validate}
                  onSubmit={( values ) => submitForm( values as AdminDocRequestTemplateForm )}
                  initialValues={data}
                  body={(
                    <div className="mt-3">
                      <Row>
                        <Col md={8} className="mt-4 mx-auto">
                          <TextField
                            field="nombre"
                            label="admin.doctemplate.name"
                            appendIcon="icon-folder"
                          />
                          <TextField
                            field="descripcion"
                            label="admin.doctemplate.description"
                            appendIcon="icon-folder"
                          />
                          <Row>
                            <Col>
                              <Checkbox
                                field="activo"
                                label="admin.doctemplate.active"
                                type="switch"
                                reverse
                              />
                            </Col>
                            <Col>
                              <Checkbox
                                field="requerido"
                                label="admin.doctemplate.required"
                                type="switch"
                                reverse
                              />
                            </Col>
                          </Row>
                          <Button
                            variant="primary"
                            type="submit"
                            className="btn--full-width mt-4"
                          >
                            {t( 'admin.common.create' )}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  )}
                />
              )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default CreateDocRequestTemplateModal;
